import type React from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Package, Bell, User, ShoppingBag } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { notificationsApi } from '@/services/api/notificationsApi';
import { queryKeys } from '@/services/api/queryClient';
import { TourHelpButton } from '@/components/ui';
import { triggerTourRestart } from '@/hooks/useTourRestart';
import { cn } from '@/lib/utils';

const NAV_ITEMS = [
  { path: '/my-orders', label: 'Заказы', icon: ShoppingBag, tourId: 'customer:nav-orders' },
  { path: '/my-notifications', label: 'Уведомления', icon: Bell, tourId: 'customer:nav-notifications' },
  { path: '/my-profile', label: 'Профиль', icon: User, tourId: 'customer:nav-profile' },
] as const;

const CustomerLayout: React.FC = () => {
  const { user, isAuthenticated } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  const { data: unreadCount } = useQuery({
    queryKey: queryKeys.unreadCount,
    queryFn: notificationsApi.getUnreadCount,
    enabled: isAuthenticated,
    refetchInterval: 30_000,
    staleTime: 15_000,
  });

  return (
    <div className="min-h-screen bg-gray-50 pb-20 sm:pb-0">
      <header data-tour="customer:header" className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
          <button
            onClick={() => navigate('/my-orders')}
            className="flex items-center gap-3"
          >
            <div className="w-9 h-9 rounded-xl bg-primary-600 flex items-center justify-center">
              <Package className="w-5 h-5 text-white" />
            </div>
            <span className="font-semibold text-gray-900">AFlow</span>
          </button>

          {user && (
            <div className="hidden sm:flex items-center gap-1">
              {NAV_ITEMS.map(item => {
                const isActive = location.pathname.startsWith(item.path);
                const Icon = item.icon;
                const showBadge = item.path === '/my-notifications' && (unreadCount ?? 0) > 0;

                return (
                  <button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    data-tour={item.tourId}
                    className={cn(
                      'relative flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200',
                      isActive
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700',
                    )}
                  >
                    <Icon className="w-4 h-4" />
                    {item.label}
                    {showBadge && (
                      <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold leading-none">
                        {unreadCount! > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          <TourHelpButton onClick={triggerTourRestart} dataTour="customer:help-btn" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        <Outlet />
      </main>

      {user && (
        <nav className="sm:hidden fixed bottom-0 left-0 right-0 z-30 bg-white/90 backdrop-blur-md border-t border-gray-100">
          <div className="flex items-center justify-around h-16 max-w-4xl mx-auto">
            {NAV_ITEMS.map(item => {
              const isActive = location.pathname.startsWith(item.path);
              const Icon = item.icon;
              const showBadge = item.path === '/my-notifications' && (unreadCount ?? 0) > 0;

              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={cn(
                    'relative flex flex-col items-center gap-0.5 px-4 py-1 rounded-xl transition-colors',
                    isActive ? 'text-primary-600' : 'text-gray-400',
                  )}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-[10px] font-medium">{item.label}</span>
                  {showBadge && (
                    <span className="absolute -top-0.5 right-1 min-w-[16px] h-4 px-0.5 flex items-center justify-center rounded-full bg-red-500 text-white text-[9px] font-bold leading-none">
                      {unreadCount! > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </nav>
      )}
    </div>
  );
};

export default CustomerLayout;
